import React from "react";
import {
  Camera,
  Fingerprint,
  Network,
  Cable,
  Wind,
  HardHat,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    id: "cctv",
    num: "01",
    icon: Camera,
    title: "Videovigilancia",
    description:
      "CCTV de alta definición, cámaras IP, analítica de video y monitoreo remoto 24/7 para perímetros y procesos industriales.",
    tags: ["CCTV", "IP", "Analítica", "VMS"],
    image: "https://images.unsplash.com/photo-1566060475410-1159300f046f",
  },
  {
    id: "acceso",
    num: "02",
    icon: Fingerprint,
    title: "Control de Acceso",
    description:
      "Torniquetes, lectores biométricos y tarjetas RFID con integración a nómina, visitas y zonas restringidas.",
    tags: ["Biométrico", "RFID", "Torniquetes"],
    image: "https://images.unsplash.com/photo-1776329255945-15212b50cd7f",
  },
  {
    id: "redes",
    num: "03",
    icon: Network,
    title: "Redes Empresariales",
    description:
      "Diseño e implementación de infraestructura LAN/WAN, switches gestionados, ruteo y Wi-Fi industrial de alta densidad.",
    tags: ["LAN/WAN", "Switching", "Wi-Fi 6"],
    image: "https://images.unsplash.com/photo-1680992044138-ce4864c2b962",
  },
  {
    id: "fibra",
    num: "04",
    icon: Cable,
    title: "Fibra Óptica",
    description:
      "Tendido, fusión, certificación y mantenimiento de enlaces de fibra óptica monomodo y multimodo punto a punto y FTTx.",
    tags: ["OS2", "OM4", "OTDR", "FTTx"],
    image: "https://images.pexels.com/photos/4373997/pexels-photo-4373997.jpeg",
  },
  {
    id: "hvac",
    num: "05",
    icon: Wind,
    title: "HVAC Industrial",
    description:
      "Climatización para sitios críticos, cuartos de servidores y procesos productivos. Eficiencia energética y monitoreo BMS.",
    tags: ["Chillers", "Precisión", "BMS"],
    image:
      "https://images.pexels.com/photos/16145690/pexels-photo-16145690.jpeg",
  },
  {
    id: "construccion",
    num: "06",
    icon: HardHat,
    title: "Construcción e Infraestructura",
    description:
      "Obra civil especializada, canalizaciones, site preparation, cuartos eléctricos y adecuaciones para nuevas plantas.",
    tags: ["Site Prep", "Canalización", "Obra"],
    image: "https://images.unsplash.com/photo-1767890131766-9fa1b608365d",
  },
];

const ServiceCard = ({ s, index }) => {
  const handleMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <article
      data-testid={`service-card-${s.id}`}
      onMouseMove={handleMove}
      className={`service-card reveal ${
        index % 3 === 1 ? "reveal-delay-1" : index % 3 === 2 ? "reveal-delay-2" : ""
      } group p-8 lg:p-10 flex flex-col`}
    >
      <span className="corner tl" />
      <span className="corner tr" />
      <span className="corner bl" />
      <span className="corner br" />

      <div className="flex items-start justify-between">
        <span className="font-mono-tech text-xs tracking-[0.25em] text-white/40">
          {s.num}
        </span>
        <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-[#F8BC33] group-hover:rotate-12 transition-all duration-300" />
      </div>

      <div className="mt-8 mb-6 overflow-hidden h-44 -mx-8 lg:-mx-10 border-y border-white/10">
        <img
          src={s.image}
          alt={s.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0"
        />
      </div>

      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 border border-white/15 group-hover:border-[#F8BC33] group-hover:text-[#FFD166] transition-colors">
          <s.icon className="w-5 h-5" strokeWidth={1.5} />
        </span>
        <h3 className="font-display font-bold text-xl lg:text-2xl">
          {s.title}
        </h3>
      </div>

      <p className="mt-4 text-sm text-white/65 leading-relaxed flex-1">
        {s.description}
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {s.tags.map((t) => (
          <span
            key={t}
            className="font-mono-tech text-[10px] uppercase tracking-widest text-white/55 border border-white/10 px-2.5 py-1"
          >
            {t}
          </span>
        ))}
      </div>
    </article>
  );
};

export const Services = () => {
  return (
    <section
      id="servicios"
      data-testid="services-section"
      className="relative py-24 lg:py-32 border-t border-white/10 bg-[#181C24]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7 reveal">
            <span className="section-tag">02 / Servicios</span>
            <h2 className="mt-6 font-display font-extrabold text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[0.98]">
              Seis verticales críticas,{" "}
              <span className="text-[#F8BC33]">un solo aliado</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 reveal reveal-delay-1">
            <p className="text-white/65 leading-relaxed">
              Integramos extremo a extremo cada capa de su infraestructura
              tecnológica: desde la canalización en obra negra hasta el último
              firmware del switch principal. Reducimos proveedores, simplificamos
              la operación.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} s={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
